import { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { TextField } from "../common/inputFiled";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { ViewColleagueModal } from "./viewcolleaguemodal";
import { LocationModal } from "./locationModel";
// Mappls picker kept for future rollback — switch import below to re-enable.
// import { MapplsMapPicker } from "./mapplsMapPicker";
import { GoogleMapPicker } from "./googleMapPicker";
import usePaymentStatus from "../../hooks/usePaymentStatus";

// ─── Module-level constants ────────────────────────────────────────────────────

const CAPITALIZE_FIELDS = new Set([
  "course_name", "edu_institution_name", "edu_address_building_name",
  "edu_address_street_name", "edu_address_google_map",
]);

const isValidUrl = (val) => {
  const text = String(val || "").trim();
  if (!text) return true;
  if (/\s/.test(text)) return false;
  return /^(?:www\.|https:\/\/(?:www\.)?)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,10}(?:\/\S*)?$/i.test(text);
};

const onlyDigits = (val) => String(val).replace(/\D+/g, "");

const isPlusCodePart = (value = "") =>
  /^[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(value || "").trim());

const cleanMapDetail = (value = "") => {
  const text = String(value || "").trim();
  return isPlusCodePart(text) ? "" : text;
};

const deriveStreetFromAddress = (address = "") => {
  const parts = String(address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !isPlusCodePart(part));
  return parts[0] || "";
};

const CURRENT_YEAR = new Date().getFullYear();

const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1950 + 1 }, (_, i) => {
  const yr = CURRENT_YEAR - i;
  return { value: String(yr), label: String(yr) };
});

const MONTH_OPTIONS = [
  ...Array.from({ length: 71 }, (_, i) => ({
    value: String(i + 1),
    label: i + 1 === 1 ? "1 month" : `${i + 1} months`,
  })),
  { value: "72+", label: "72+ months" },
];

const EDUCATION_LEVEL_OPTIONS = [
  { value: "Primary", label: "Primary" },
  { value: "Secondary", label: "Secondary" },
  { value: "Grade 10", label: "Grade 10" },
  { value: "Grade 12", label: "Grade 12" },
  { value: "Graduation", label: "Graduation" },
  { value: "Post Graduation", label: "Post Graduation" },
  { value: "Doctorate", label: "Doctorate" },
  { value: "Vocational Course", label: "Vocational Course" },
  { value: "Certification", label: "Certification" },
];

const PROGRAM_DELIVERY_OPTIONS = [
  { value: "Full time", label: "Full time" },
  { value: "Part Time/Weekend", label: "Part Time/Weekend" },
  { value: "Blended- Online+ Offline", label: "Blended- Online+ Offline" },
  { value: "Online", label: "Online" },
];

const INITIAL_FORM = {
  education_id: "",
  education_current_studying: "0",
  edu_institution_name: "",
  edu_pin_code: "",
  edu_city_region_country: "",
  edu_address_pincode: "",
  edu_address_google_map: "",
  course_name: "",
  edu_start_date: "",
  edu_end_date: "",
  education_level: "",
  edu_address_website: "",
  edu_duration_of_program: "",
  edu_program_delivery: "",
  edu_address_building_number: "",
  edu_address_building_name: "",
  edu_address_latitude: "",
  edu_address_longitude: "",
  edu_address_area_locality: "",
  edu_address_city: "",
  edu_address_region: "",
  edu_address_country: "",
  edu_address_street_name: "",
  created_at: "",
  edu_contact_number: "",
};

// Plan-based field visibility
const ALL_PLAN_FIELDS = new Set([
  "edu_institution_name", "edu_pin_code", "edu_city_region_country",
  "edu_address_pincode", "edu_address_city", "edu_address_region", "edu_address_country",
  "course_name", "edu_start_date", "edu_end_date",
]);

const PLAN2_EXTRA_FIELDS = new Set([
  "education_level", "edu_address_website",
]);

const PLAN3_EXTRA_FIELDS = new Set([
  "edu_duration_of_program", "edu_program_delivery",
  "edu_address_building_number", "edu_address_building_name", "edu_address_street_name", "edu_address_google_map",
]);

// Required fields per plan
const REQUIRED_ALL = new Set([
  "edu_institution_name", "edu_pin_code", "edu_city_region_country",
  "course_name", "edu_start_date", "edu_end_date",
]);

const REQUIRED_PLAN2_EXTRA = new Set([
  "education_level",
]);

const REQUIRED_PLAN3_EXTRA = new Set([
  "edu_program_delivery", "edu_address_building_name", "edu_address_google_map",
]);

// Explicit API payload sets — only these fields are sent per plan
// NEVER sent: edu_contact_number, created_at
const PAYLOAD_ALL = new Set([
  "education_id",
  "edu_institution_name", "edu_pin_code", "edu_city_region_country", "edu_address_pincode",
  "edu_address_city", "edu_address_region", "edu_address_country",
  "course_name", "edu_start_date", "edu_end_date",
]);

const PAYLOAD_PLAN2_EXTRA = new Set([
  "education_level", "edu_address_website",
]);

const PAYLOAD_PLAN3_EXTRA = new Set([
  "edu_duration_of_program", "edu_program_delivery",
  "edu_address_building_number", "edu_address_building_name", "edu_address_street_name", "edu_address_google_map",
  "edu_address_latitude", "edu_address_longitude",
]);

// Ordered field definitions — filtered by plan at render time
const FIELD_DEFS = [
  { key: "edu_institution_name", label: "Name of the Institution" },
  { key: "edu_pin_code", label: "Pincode", type: "number" },
  { key: "edu_city_region_country", label: "City, Region and Country" },
 // Plan 2+
  { key: "education_level", label: "Education Level", type: "select", options: EDUCATION_LEVEL_OPTIONS },
  { key: "course_name", label: "Course Name" },
  { key: "edu_start_date", label: "Start Date", type: "month" },
  { key: "edu_end_date", label: "End Date", type: "month" },
  // Plan 3 only
  { key: "edu_duration_of_program", label: "Duration of Program", type: "select", options: MONTH_OPTIONS },
  { key: "edu_program_delivery", label: "Program Delivery", type: "select", options: PROGRAM_DELIVERY_OPTIONS },
  { key: "edu_address_building_number", label: "Building Number/Office Number" },
  { key: "edu_address_building_name", label: "Building Name" },
  { key: "edu_address_pincode", label: "Pincode", type: "number" },
  { key: "edu_address_street_name", label: "Street Name" },
  { key: "edu_address_google_map", label: "Address" },
  { key: "edu_address_website", label: "Website" },
];

const INSTITUTION_COURSE_FIELDS = [
  "edu_institution_name",
  "edu_pin_code",
  "edu_city_region_country",
  "education_level",
  "course_name",
  "edu_start_date",
  "edu_end_date",
  "edu_duration_of_program",
  "edu_program_delivery",
];

const INSTITUTION_ADDRESS_FIELDS = [
  "edu_address_building_number",
  "edu_address_building_name",
  "edu_address_pincode",
  "edu_address_street_name",
  "edu_address_google_map",
  "edu_address_website",
];

const HIDDEN_EDU_ADDRESS_FIELDS = new Set([
  "edu_address_pincode",
  "edu_address_street_name",
]);

// ─── Component ─────────────────────────────────────────────────────────────────

export const EducationPage = ({ onSave }) => {
  const { planType, isPaymentActive, loading: paymentStatusLoading } = usePaymentStatus();
  const token = useRef(Cookies.get("auth_token")).current;
  const [isEditing, setIsEditing] = useState(false);
  const [openCourse, setOpenCourse] = useState(true);
  const [openAddress, setOpenAddress] = useState(true);
  const [addEducation, setAddEducation] = useState(false);
  const [educationList, setEducationList] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showGoogleMapPicker, setShowGoogleMapPicker] = useState(false);
  const [locationSource, setLocationSource] = useState("address");
  const [mapInitialSearch, setMapInitialSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, educationId: null });

  const formScrollRef = useRef(null);
  const fieldRefs = useRef({});

  const [educationData, setEducationData] = useState(INITIAL_FORM);
  const lastPincodeToastRef = useRef(0);

  // Derived values from API plan status
  const visibleFieldKeys = useMemo(() => {
    const keys = new Set(ALL_PLAN_FIELDS);
    if (planType >= 2) PLAN2_EXTRA_FIELDS.forEach((k) => keys.add(k));
    if (planType >= 3) PLAN3_EXTRA_FIELDS.forEach((k) => keys.add(k));
    return keys;
  }, [planType]);

  const requiredKeys = useMemo(() => {
    const keys = new Set(REQUIRED_ALL);
    if (planType >= 2) REQUIRED_PLAN2_EXTRA.forEach((k) => keys.add(k));
    if (planType >= 3) REQUIRED_PLAN3_EXTRA.forEach((k) => keys.add(k));
    return keys;
  }, [planType]);

  const canUseGoogleMapAddress = visibleFieldKeys.has("edu_address_google_map");

  const visibleAddressFields = useMemo(() => {
    const fields = FIELD_DEFS.filter((f) => visibleFieldKeys.has(f.key) && INSTITUTION_ADDRESS_FIELDS.includes(f.key));
    const hasGoogleAddress = planType >= 3 && String(educationData.edu_address_google_map || "").trim() !== "";
    if (!hasGoogleAddress) return fields;
    return fields.filter((f) => !["edu_address_city", "edu_address_region", "edu_address_country"].includes(f.key));
  }, [visibleFieldKeys, planType, educationData.edu_address_google_map]);

  const isPincodeDisabledByAddress = planType >= 3 && Boolean(String(educationData.edu_address_google_map || "").trim());

  useEffect(() => {
    fetchEducationDetails();
  }, []);

  const fetchEducationDetails = async () => {
    setLoadingEducation(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get_education`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "1" && Array.isArray(data.data?.[0]?.education)) {
        setEducationList(data.data[0].education);
      } else {
        toast.dismiss();
        toast.error(data.message || "No education details found");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    } finally {
      setLoadingEducation(false);
    }
  };

  const handleChange = (field, value) => {
    const titleCaseFields = new Set(["course_name", "edu_institution_name", "edu_address_building_name"]);
    const lettersOnlyFields = new Set(["course_name"]);
    let normalizedValue = typeof value === "string" ? value.replace(/^\s+/, "") : value;
    if (lettersOnlyFields.has(field)) {
      normalizedValue = String(normalizedValue || "").replace(/[^a-zA-Z\s]/g, "");
    }
    const toTitleCase = (str) => {
      if (!str) return str;
      return str
        .split(" ")
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
        .join(" ");
    };

    const websiteNoSpaceValue = field === "edu_address_website" ? String(normalizedValue || "").replace(/\s+/g, "") : normalizedValue;
    const processedValue = titleCaseFields.has(field) && websiteNoSpaceValue ? toTitleCase(websiteNoSpaceValue) : websiteNoSpaceValue;

    setEducationData((prev) => {
      const next = { ...prev, [field]: processedValue };
      if (field === "edu_pin_code") next.edu_address_pincode = processedValue;
      if (field === "edu_address_pincode") next.edu_pin_code = processedValue;
      return next;
    });
  };

  const scrollToFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    const el = fieldRefs.current[firstKey];
    const container = formScrollRef.current;
    if (el) {
      const input = el.querySelector("input, select, textarea");
      if (container) {
        const offsetTop = el.offsetTop - 24;
        container.scrollTo({ top: offsetTop < 0 ? 0 : offsetTop, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (input && typeof input.focus === "function") input.focus();
    }
    toast.error("Please fix the highlighted fields");
  };

  const getSectionForField = (field) => {
    if (["edu_address_building_number", "edu_address_building_name", "edu_address_pincode", "edu_address_street_name", "edu_address_google_map", "edu_address_website"].includes(field)) {
      return "address";
    }
    return "course";
  };

  const handleSubmit = async () => {
    const newErrors = {};
    const isCurrentStudying = Number(educationData.education_current_studying) === 1;

    for (const key of requiredKeys) {
      if (isCurrentStudying && key === "edu_end_date") continue;
      if (!educationData[key] || String(educationData[key]).trim() === "") {
        const def = FIELD_DEFS.find((f) => f.key === key);
        newErrors[key] = `Enter ${def?.label || key}`;
      }
    }

    if (planType >= 2 && educationData.edu_address_website && !isValidUrl(educationData.edu_address_website))
      newErrors.edu_address_website = "Enter a valid website";

    if (educationData.edu_address_pincode) {
      if (onlyDigits(educationData.edu_address_pincode).length !== 6)
        newErrors.edu_address_pincode = "Pin Code must be exactly 6 digits";
    }

    if (educationData.edu_duration_of_program && Number(educationData.edu_duration_of_program) <= 0)
      newErrors.edu_duration_of_program = "Duration must be greater than 0";

    // Validate month inputs (format can be YYYY-MM from <input type="month"> or MM/YYYY)
    const parseMonth = (val) => {
      if (!val) return null;
      if (typeof val !== "string") return null;
      if (val.includes("-")) {
        const [y, m] = val.split("-");
        const yi = Number(y);
        const mi = Number(m);
        if (Number.isNaN(yi) || Number.isNaN(mi)) return null;
        return { y: yi, m: mi };
      }
      if (val.includes("/")) {
        const [m, y] = val.split("/");
        const yi = Number(y);
        const mi = Number(m);
        if (Number.isNaN(yi) || Number.isNaN(mi)) return null;
        return { y: yi, m: mi };
      }
      return null;
    };

    const start = parseMonth(educationData.edu_start_date);
    const end = parseMonth(educationData.edu_end_date);

    if (start) {
      if (start.y < 1950 || start.y > CURRENT_YEAR) newErrors.edu_start_date = `Start Date year must be between 1950 and ${CURRENT_YEAR}`;
    }
    if (end) {
      if (end.y < 1950 || end.y > CURRENT_YEAR) newErrors.edu_end_date = `End Date year must be between 1950 and ${CURRENT_YEAR}`;
    }
    if (!isCurrentStudying && start && end) {
      if (start.y > end.y || (start.y === end.y && start.m > end.m)) {
        newErrors.edu_end_date = "End date must be the same or after start date";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      const firstSection = getSectionForField(firstKey);
      if (firstSection === "course") setOpenCourse(true);
      if (firstSection === "address") setOpenAddress(true);
      setErrors(newErrors);
      setTimeout(() => scrollToFirstError(newErrors), 0);
      return;
    }

    try {
      // Build whitelist of allowed keys for this plan
      const allowedKeys = new Set([...PAYLOAD_ALL]);
      if (planType >= 2) PAYLOAD_PLAN2_EXTRA.forEach((k) => allowedKeys.add(k));
      if (planType >= 3) PAYLOAD_PLAN3_EXTRA.forEach((k) => allowedKeys.add(k));

      const payload = new URLSearchParams();
      for (const [key, val] of Object.entries(educationData)) {
        if (!allowedKeys.has(key)) continue;
        payload.set(key, val ?? "");
      }
      // Convert month inputs from `YYYY-MM` (browser month input) to `MM/YYYY` as requested
      const convertMonthValue = (v) => {
        if (!v) return "";
        if (v.includes("-")) {
          const [y, m] = v.split("-");
          return `${m.padStart(2, "0")}/${y}`;
        }
        if (v.includes("/")) {
          const [m, y] = v.split("/");
          return `${String(m).padStart(2, "0")}/${y}`;
        }
        return v;
      };
      if (allowedKeys.has("edu_start_date")) {
        payload.set("edu_start_date", convertMonthValue(educationData.edu_start_date));
      }
      if (allowedKeys.has("edu_end_date")) {
        payload.set("edu_end_date", isCurrentStudying ? "" : convertMonthValue(educationData.edu_end_date));
      }
      // Keep backward compatibility for requested/legacy backend keys.
      payload.set("edu_institution_name", educationData.edu_institution_name || "");
      payload.set("edu_address_pincode", educationData.edu_address_pincode || educationData.edu_pin_code || "");
      payload.set("edu_pin_code", educationData.edu_pin_code || educationData.edu_address_pincode || "");
      payload.set("education_current_studying", educationData.education_current_studying || "0");

      const endpoint = selectedId
        ? `${process.env.REACT_APP_API_BASE_URL}/update_education`
        : `${process.env.REACT_APP_API_BASE_URL}/insert_education`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });

      const data = await res.json();
      if (data.status === "1") {
        toast.success(selectedId ? "Education updated successfully!" : "Education added successfully!");
        setErrors({});
        setAddEducation(false);
        setIsEditing(false);
        setSelectedId(null);
        fetchEducationDetails();
        if (typeof onSave === "function") onSave();
      } else {
        toast.dismiss();
        toast.error(data.message || "Failed to update education");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    }
  };

  const handleAddClick = () => {
    setSelectedId(null);
    setEducationData(INITIAL_FORM);
    setErrors({});
    setAddEducation(true);
    setIsEditing(true);
    setShowLocationModal(false);
    setLocationSource("address");
  };

  const handleCardClick = (edu) => {
    const toInputMonth = (v) => {
      if (!v) return "";
      if (typeof v !== "string") return "";
      // already YYYY-MM
      if (v.includes("-")) {
        const [y, m] = v.split("-");
        if (y && m && y.length === 4) return `${y}-${String(m).padStart(2, "0")}`;
      }
      // MM/YYYY or M/YYYY
      if (v.includes("/")) {
        const [m, y] = v.split("/");
        if (y && m) return `${y}-${String(m).padStart(2, "0")}`;
      }
      // year only
      if (/^\d{4}$/.test(v)) return `${v}-01`;
      return "";
    };

    const initial = { ...INITIAL_FORM, ...edu };
    initial.edu_pin_code = edu?.edu_pin_code || edu?.edu_address_pincode || edu?.pin_code || "";
    initial.edu_address_pincode = initial.edu_pin_code;
    initial.edu_city_region_country = edu?.edu_city_region_country || edu?.edu_address_google_map || "";
    initial.edu_institution_name = edu?.edu_institution_name || "";
    initial.edu_address_google_map = edu?.edu_address_google_map || "";
    initial.education_current_studying = edu?.education_current_studying ?? "0";
    initial.edu_start_date = toInputMonth(initial.edu_start_date);
    initial.edu_end_date = Number(initial.education_current_studying) === 1 ? "" : toInputMonth(initial.edu_end_date);
    setSelectedId(edu.education_id);
    setEducationData(initial);
    setErrors({});
    setAddEducation(true);
    setIsEditing(false);
    setShowLocationModal(false);
    setLocationSource("address");
  };

  const handleToggleCurrentSelect = () => {
    const isCurrent = Number(educationData.education_current_studying) === 1;
    setEducationData((prev) => ({
      ...prev,
      education_current_studying: isCurrent ? 0 : 1,
      edu_end_date: isCurrent ? prev.edu_end_date : "",
    }));
  };

  const openGoogleMapFromAddressField = () => {
    if (!isEditing) return;
    const seed = [
      educationData.edu_pin_code || educationData.edu_address_pincode,
      educationData.edu_city_region_country,
      educationData.edu_address_google_map,
    ].filter(Boolean).join(", ");
    setMapInitialSearch(seed);
    setShowGoogleMapPicker(true);
  };

  // Step 1: user picks a place from LocationModal
  const handlePlaceSelect = ({ place, location, country, post_code }) => {
    const source = location || place || {};
    const placeName =
      place?.place_name ||
      place?.name ||
      place?.company_name ||
      place?.["place name"] ||
      source?.place_name ||
      source?.name ||
      source?.company_name ||
      source?.city ||
      "";
    const state = source?.state || source?.region || place?.state || place?.state_abbreviation || "";
    const countryValue = country || source?.country || source?.["country code"] || "";
    const latitude =
      place?.latitude ||
      source?.latitude ||
      source?.geometry?.lat ||
      source?.geometry?.location?.lat ||
      source?.places?.[0]?.latitude ||
      "";
    const longitude =
      place?.longitude ||
      source?.longitude ||
      source?.geometry?.lng ||
      source?.geometry?.location?.lng ||
      source?.places?.[0]?.longitude ||
      "";
    setShowLocationModal(false);

    const cityRegionCountry = [location?.city || placeName, state, countryValue].filter(Boolean).join(", ");
    setEducationData((prev) => ({
      ...prev,
      edu_city_region_country: cityRegionCountry,
      edu_address_pincode: post_code || "",
      edu_pin_code: post_code || "",
      edu_address_city: location?.city || placeName || "",
      edu_address_region: state || "",
      edu_address_country: countryValue || "",
      edu_address_latitude: latitude || "",
      edu_address_longitude: longitude || "",
    }));
    setErrors((prev) => ({
      ...prev,
      edu_city_region_country: null,
      edu_address_pincode: null,
      edu_pin_code: null,
      edu_address_city: null,
      edu_address_region: null,
      edu_address_country: null,
      edu_address_latitude: null,
      edu_address_longitude: null,
    }));
    toast.success("Address updated from location!");
    setLocationSource("address");
  };

  // Step 2: user confirms pin on Google Map
  const handleSelectLocation = (item) => {
    const previousPin = String(educationData.edu_pin_code || educationData.edu_address_pincode || "");
    const selectedPin = String(item?.pin_code || "");
    const pinChanged = Boolean(selectedPin) && selectedPin !== previousPin;
    const cityRegionCountry =
      [item?.city, item?.region, item?.country].filter(Boolean).join(", ") ||
      item?.edu_city_region_country ||
      item?.formatted_address ||
      "";
    const selectedAddress =
      item?.formatted_address ||
      item?.work_address ||
      item?.google_map_link ||
      item?.edu_city_region_country ||
      "";
    setEducationData((prev) => ({
      ...prev,
      edu_city_region_country: cityRegionCountry || prev.edu_city_region_country,
      edu_address_google_map: selectedAddress || prev.edu_address_google_map,
      edu_address_pincode: selectedPin || prev.edu_address_pincode,
      edu_pin_code: selectedPin || prev.edu_pin_code,
      edu_address_latitude: item?.latitude ? String(item.latitude) : "",
      edu_address_longitude: item?.longitude ? String(item.longitude) : "",
      edu_address_street_name: cleanMapDetail(item?.street_name) || deriveStreetFromAddress(selectedAddress),
      edu_address_building_number: cleanMapDetail(item?.flat_number),
      edu_address_building_name: cleanMapDetail(item?.building_name),
      edu_address_area_locality: item?.area_locality || "",
      edu_address_city: item?.city || "",
      edu_address_region: item?.region || "",
      edu_address_country: item?.country || "",
    }));
    if (pinChanged) {
      toast.success(`Address updated. Pincode changed to ${selectedPin}.`);
    } else {
      toast.success("Address updated from location!");
    }
    setErrors((prev) => ({
      ...prev,
      edu_city_region_country: null,
      edu_address_google_map: null,
      edu_address_pincode: null,
      edu_pin_code: null,
      edu_address_city: null,
      edu_address_region: null,
      edu_address_country: null,
      edu_address_latitude: null,
      edu_address_longitude: null,
    }));
    setShowGoogleMapPicker(false);
    setMapInitialSearch("");
  };

  const handleDelete = (e, educationId) => {
    e.stopPropagation();
    setDeleteModal({ open: true, educationId });
  };

  const confirmDelete = async () => {
    const { educationId } = deleteModal;
    setDeleteModal({ open: false, educationId: null });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/delete_education`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: new URLSearchParams({ education_id: educationId }).toString(),
      });
      const data = await res.json();
      if (data.status === "1") {
        toast.success("Education deleted successfully!");
        fetchEducationDetails();
      } else {
        toast.error(data.message || "Failed to delete education");
      }
    } catch {
      toast.error("Failed to delete education");
    }
  };
  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "U";
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const formatEducationAddress = (edu) => {
    const addressParts = [
      cleanMapDetail(edu?.edu_address_building_name),
      cleanMapDetail(edu?.edu_address_building_number),
      cleanMapDetail(edu?.edu_address_street_name),
      edu?.edu_address_area_locality,
      edu?.edu_address_city,
      edu?.edu_address_region,
      edu?.edu_address_country,
      edu?.edu_address_pincode || edu?.edu_pin_code,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }

    return edu?.edu_city_region_country || edu?.edu_address_google_map || "—";
  };

  const formatStudyPeriod = (edu) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const parseMonth = (value) => {
      const text = String(value || "").trim();
      if (!text) return null;

      let year, month;
      if (text.includes("-")) {
        const [y, m] = text.split("-");
        year = Number(y);
        month = Number(m);
      } else if (text.includes("/")) {
        const [m, y] = text.split("/");
        year = Number(y);
        month = Number(m);
      } else if (/^\d{4}$/.test(text)) {
        year = Number(text);
        month = 1;
      } else {
        return null;
      }

      if (!Number.isNaN(year) && !Number.isNaN(month) && month >= 1 && month <= 12) {
        return { year, month };
      }
      return null;
    };

    const fmt = (m) => `${(monthNames[m.month - 1] ?? "").slice(0,3)} ${m.year}`;

    const start = parseMonth(edu?.edu_start_date);
    const end = parseMonth(edu?.edu_end_date);

    if (!start && !end) return "—";

    if (Number(edu?.education_current_studying) === 1) {
      if (!start) return "—";
      return `${fmt(start)} - Present`;
    }

    if (start && end) {
      return `${fmt(start)} - ${fmt(end)}`;
    }

    if (start && !end) return `${fmt(start)} - —`;
    if (!start && end) return `— - ${fmt(end)}`;
    return "—";
  };

  const hasVisibleFieldValue = (fieldKey) => {
    const value = educationData[fieldKey];
    return String(value ?? "").trim() !== "";
  };

  const hasPreviewableAddressField = (field) =>
    !HIDDEN_EDU_ADDRESS_FIELDS.has(field.key) && hasVisibleFieldValue(field.key);

  const shouldShowInstitutionAddressSection =
    isEditing || visibleAddressFields.some(hasPreviewableAddressField);

  const getPreviewIcon = (key) => {
    if (["edu_start_date", "edu_end_date", "education_current_studying", "edu_duration_of_program"].includes(key)) return "../input-logos/calander.svg";
    if (["edu_pin_code", "edu_address_pincode", "edu_city_region_country", "edu_address_google_map"].includes(key)) return "../input-logos/location.svg";
    if (["edu_address_building_number", "edu_address_building_name", "edu_address_street_name"].includes(key)) return "../input-logos/building.svg";
    return "../input-logos/user.svg";
  };

  const formatPreviewValue = (field, overrideValue) => {
    if (overrideValue !== undefined) return overrideValue;
    const value = educationData[field.key];
    if (!value) return "";
    if (field.type === "month" && /^\d{4}-\d{2}$/.test(String(value))) {
      const [year, month] = String(value).split("-");
      return `${month}/${year}`;
    }
    return String(value);
  };

  const renderPreviewFieldCard = (field, overrideValue) => {
    if (HIDDEN_EDU_ADDRESS_FIELDS.has(field.key)) return null;
    const value = formatPreviewValue(field, overrideValue);
    if (!value) return null;

    return (
      <div
        key={field.key}
        className={`${field.key === "edu_address_google_map" ? "min-[1100px]:col-span-2" : ""} flex items-center gap-3 px-4 py-4 border-b last:border-b-0`}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
          <img src={getPreviewIcon(field.key)} alt="" className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-xs text-gray-500">{field.label}</div>
          <div className="text-sm font-medium text-gray-900 break-words">{value || "—"}</div>
        </div>
      </div>
    );
  };

  const renderCurrentStatusPreview = (label) => (
    <div className="min-[1100px]:col-span-2 px-4 py-4 border-b last:border-b-0">
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#6938EF]">
          <span className="absolute right-1 h-4 w-4 rounded-full bg-white shadow-sm" />
        </span>
      </div>
    </div>
  );

  // Renders a single form field based on its FIELD_DEFS entry
  const renderField = ({ key, label, type, options }) => {
    const isCurrent = Number(educationData.education_current_studying) === 1;
    const isEndDate = key === "edu_end_date";
    const startMonthValue = String(educationData.edu_start_date || "").trim();
    const endMonthValue = String(educationData.edu_end_date || "").trim();
    const isCityRegionCountry = key === "edu_city_region_country";
    const isRequired = requiredKeys.has(key) && !(isCurrent && isEndDate);
    const clearError = (k) => {
      if (errors[k]) setErrors((p) => ({ ...p, [k]: null }));
    };

    if (isEndDate && isCurrent) {
      return null;
    }

    if (!isEditing && !hasVisibleFieldValue(key)) {
      return null;
    }

    if (type === "select") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            name={key}
            required={isRequired}
            select
            options={options}
            value={educationData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            disabled={!isEditing || isCityRegionCountry}
            error={errors[key]}
          />
        </div>
      );
    }

    if (type === "month") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            type="month"
            name={key}
            placeholder="MM YYYY"
            required={isRequired}
            value={educationData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            disabled={!isEditing || isCityRegionCountry}
            error={errors[key]}
            minMonth={key === "edu_end_date" ? startMonthValue : undefined}
            maxMonth={key === "edu_start_date" && !isCurrent ? endMonthValue : undefined}
          />
        </div>
      );
    }

    if (HIDDEN_EDU_ADDRESS_FIELDS.has(key)) {
      return null;
    }

    if (key === "edu_pin_code") {
      const showPincodeDerivedToast = () => {
        const now = Date.now();
        if (now - (lastPincodeToastRef.current || 0) > 1000) {
          toast.dismiss();
          toast("Pincode is derived from Address. To change it, edit the Address field and it will update automatically.", {
            icon: "ℹ️",
          });
          lastPincodeToastRef.current = now;
        }
      };

      return (
        <div
          key={key}
          ref={(el) => (fieldRefs.current[key] = el)}
          onClick={() => {
            if (isEditing && isPincodeDisabledByAddress) {
              showPincodeDerivedToast();
            }
          }}
          className={isPincodeDisabledByAddress && isEditing ? "cursor-not-allowed" : undefined}
        >
          <TextField
            label={label}
            type="number"
            placeholder={isPincodeDisabledByAddress ? "Derived from Address" : "Click to select pincode"}
            name={key}
            required={isRequired}
            value={educationData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            onClick={() => {
              if (!isEditing || isPincodeDisabledByAddress) return;
              setLocationSource("pincode");
              setShowLocationModal(true);
            }}
            onFocus={(e) => {
              if (!isEditing) return;
              if (isPincodeDisabledByAddress) {
                e.target.blur();
                return;
              }
              e.target.blur();
              setLocationSource("pincode");
              setShowLocationModal(true);
            }}
            readOnly
            disabled={!isEditing || isPincodeDisabledByAddress}
            rightIcon={isEditing && !isPincodeDisabledByAddress ? "../input-logos/location.svg" : undefined}
            title={isPincodeDisabledByAddress
              ? "Pincode is derived from Address. Edit the Address field to update it."
              : undefined}
            error={errors[key]}
          />
        </div>
      );
    }

    if (type === "number") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            type="number"
            placeholder={`Enter ${label}`}
            name={key}
            required={isRequired}
            value={educationData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            disabled={!isEditing || isCityRegionCountry}
            error={errors[key]}
          />
        </div>
      );
    }

    if (key === "edu_address_google_map") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            placeholder="Click to pick location from map"
            name={key}
            required={isRequired}
            value={educationData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            onClick={openGoogleMapFromAddressField}
            onFocus={(e) => {
              if (!isEditing) return;
              e.target.blur();
              openGoogleMapFromAddressField();
            }}
            readOnly={isEditing}
            disabled={!isEditing}
            error={errors[key]}
          />
        </div>
      );
    }

    return (
      <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
        <TextField
          label={label}
          placeholder={`Enter ${label}`}
          name={key}
          required={isRequired}
          value={educationData[key] ?? ""}
          onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
          disabled={!isEditing || isCityRegionCountry}
          error={errors[key]}
        />
      </div>
    );
  };

  if (paymentStatusLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AF8]" />
      </div>
    );
  }

  if (!isPaymentActive) return (
    <div className="w-full flex items-center justify-center h-[90vh] p-6">
      <div className="max-w-5xl w-full bg-white rounded-2xl p-8 shadow-lg grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        {/* Left column: text, perks, CTA */}
        <div className="px-2 md:px-6">
          <div className="w-20 h-20 rounded-full bg-[#F6F4FF] flex items-center justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="g1a" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#BFA7FF"/>
                  <stop offset="100%" stopColor="#7B4DFF"/>
                </linearGradient>
              </defs>
              <circle cx="40" cy="55" r="15" fill="url(#g1a)"/>
              <circle cx="80" cy="55" r="15" fill="url(#g1a)"/>
            </svg>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-2">No Active Plan</h3>
          <p className="text-gray-600 mb-6">You don't have an active plan yet. Choose a plan to unlock all the features and make the most of  PhoneBookPlus.</p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#BFA7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <circle cx="40" cy="55" r="15" fill="url(#icon1)"/>
                <circle cx="80" cy="55" r="15" fill="url(#icon1)"/>
                <path d="M35 25C50 10 70 10 85 25" stroke="#8D63FF" strokeWidth="6" strokeLinecap="round"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Build stronger connections</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon2" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#CDB7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <path d="M60 10L100 25V55C100 82 82 102 60 110C38 102 20 82 20 55V25L60 10Z" fill="url(#icon2)"/>
                <rect x="42" y="50" width="36" height="28" rx="6" fill="#8D63FF"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Keep your data secure</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon3" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#D5C4FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <rect x="20" y="65" width="18" height="35" rx="4" fill="url(#icon3)"/>
                <rect x="48" y="50" width="18" height="50" rx="4" fill="url(#icon3)"/>
                <rect x="76" y="35" width="18" height="65" rx="4" fill="url(#icon3)"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Boost productivity and grow</p>
            </div>
          </div>

          <button onClick={() => window.location.href = "/payment"} className="mt-2 inline-flex items-center gap-3 bg-[#6D5EF7] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:bg-[#5b49e8] transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Choose a Plan
          </button>
        </div>

        {/* Right column: illustration */}
        <div className="flex items-center justify-center p-4">
          <svg width="320" height="240" viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-w-full h-auto">
            <defs>
              <linearGradient id="illGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#F3EEFF"/>
                <stop offset="100%" stopColor="#F8F6FF"/>
              </linearGradient>
            </defs>
            <rect x="20" y="20" width="220" height="200" rx="16" fill="url(#illGrad)" stroke="#EDE7FF" strokeWidth="2"/>
            <rect x="52" y="44" width="156" height="14" rx="7" fill="#FFFFFF"/>
            <rect x="52" y="72" width="156" height="12" rx="6" fill="#EDE7FF"/>
            <rect x="52" y="96" width="120" height="12" rx="6" fill="#EDE7FF"/>
            <circle cx="260" cy="40" r="28" fill="#F6EEFF"/>
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteModal.open &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm text-center">
              <div className="mb-4 flex justify-center flex-col items-center">
                <div className="h-14 w-14 flex mb-4 justify-center items-center bg-[linear-gradient(180deg,#8862F2_0%,#7544FC_29%,#40248B_100%)] rounded-full !p-0 shadow-sm transition-opacity">
                  <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-[#6938EF] mb-2">Delete Education?</h3>
                <p className="text-gray-600">Are you sure you want to delete this education? This action cannot be undone.</p>
              </div>
              <div className="flex gap-4 justify-center mt-6">
                <button
                  className="px-6 py-2 rounded-full bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
                  onClick={() => setDeleteModal({ open: false, educationId: null })}
                >
                  Cancel
                </button>
                <button className="px-6 py-2 rounded-full submit-button font-semibold" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {addEducation ? (
        <div>
          <div className="rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <button
                  aria-label="Back to previous page"
                  onClick={() => setAddEducation(false)}
                  className="flex items-center aoboshi-one text-lg text-[#212121] font-medium p-4 rounded-2xl"
                >
                  <img src="./back-arrow.svg" alt="back-arrow" className="mr-4" />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedId ? "Edit Education" : "Add Education"}
                </h2>
              </div>
              <div className="flex gap-4 items-center">
                {isEditing ? (
                  <button
                    onClick={handleSubmit}
                    type="button"
                    className="h-11 w-11 rounded-full flex items-center justify-center"
                    aria-label={selectedId ? "Update education" : "Submit education"}
                  >
                    <img src="../tick_mark.svg" alt="Submit" className="w-6 h-6" />
                  </button>
                ) : (
                  <button className="mr-1" onClick={() => setIsEditing(true)} type="button">
                    <img src="./edit.svg" alt="Edit" />
                  </button>
                )}
              </div>
            </div>

            <div className="h-[85dvh] overflow-y-auto [&::-webkit-scrollbar]:hidden" ref={formScrollRef}>
              <div className="mt-4 gap-4">
                <div className="mb-4 border border-gray-100 rounded-xl overflow-visible bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenCourse((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                      openCourse
                        ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                        : "bg-gradient-to-r from-gray-50 to-white"
                    }`}
                  >
                    <span className={`font-semibold transition-colors ${openCourse ? "text-[#6938EF]" : "text-gray-900"}`}>
                    Course Details
                    </span>
                    <span className={`transition-transform duration-300 ${openCourse ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>

                  {openCourse && (
                    <div className={`${isEditing ? "grid gap-4 px-4 pb-4 mt-4 grid-cols-1" : "grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4"}`}>
                      {isEditing ? (
                        <>
                          <div className="w-full flex justify-between my-3 pr-2" ref={(el) => (fieldRefs.current["education_current_studying"] = el)}>
                            <label className="flex items-center gap-2 cursor-pointer mb-2 w-full justify-between">
                              <span className="text-xs text-gray-500">Currently Studying<span className="text-red-500">*</span></span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={Number(educationData.education_current_studying) === 1}
                                  onChange={handleToggleCurrentSelect}
                                  disabled={!isEditing}
                                />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-[16px]" />
                              </label>
                            </label>
                          </div>

                          {FIELD_DEFS
                            .filter((f) => visibleFieldKeys.has(f.key) && INSTITUTION_COURSE_FIELDS.includes(f.key))
                            .map(renderField)}
                        </>
                      ) : (
                        <>
                          {Number(educationData.education_current_studying) === 1 &&
                            renderCurrentStatusPreview("Currently Studying")}
                          {FIELD_DEFS
                            .filter((f) => visibleFieldKeys.has(f.key) && INSTITUTION_COURSE_FIELDS.includes(f.key))
                            .filter((f) => hasVisibleFieldValue(f.key))
                            .map((f) => renderPreviewFieldCard(f))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {shouldShowInstitutionAddressSection && (
                <div className="mb-4 border border-gray-100 rounded-xl overflow-visible bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenAddress((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                      openAddress
                        ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                        : "bg-gradient-to-r from-gray-50 to-white"
                    }`}
                  >
                    <span className={`font-semibold transition-colors ${openAddress ? "text-[#6938EF]" : "text-gray-900"}`}>
                      Institution Address
                    </span>
                    <span className={`transition-transform duration-300 ${openAddress ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>

                  {openAddress && (
                    <div className={`${isEditing ? "grid gap-4 px-4 pb-4 mt-4 grid-cols-1" : "grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4"}`}>
                      {visibleAddressFields
                        .filter((field) => isEditing || hasVisibleFieldValue(field.key))
                        .map((field) => (isEditing ? renderField(field) : renderPreviewFieldCard(field)))}

                    </div>
                  )}
                </div>
                )}
              </div>


            </div>
          </div>

          {showLocationModal && (
            <LocationModal
              show={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              onSelect={handlePlaceSelect}
              initialPincode={educationData.edu_pin_code || ""}
              initialPlaceName={educationData.edu_institution_name || ""}
            />
          )}

          {showGoogleMapPicker && (
            <>
              <GoogleMapPicker
                show={showGoogleMapPicker}
                onClose={() => { setShowGoogleMapPicker(false); setMapInitialSearch(""); }}
                onSelect={handleSelectLocation}
                initialSearch={mapInitialSearch}
              />
              {/* <MapplsMapPicker
                show={showGoogleMapPicker}
                onClose={() => { setShowGoogleMapPicker(false); setMapInitialSearch(""); }}
                onSelect={handleSelectLocation}
                initialSearch={mapInitialSearch}
              /> */}
            </>
          )}
        </div>
      ) : (
        <div className="rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Education</h2>
            <button
              onClick={handleAddClick}
              className="p-2 hover:bg-gray-100 rounded-full bg-white shadow-md"
            >
              <img src="./add.svg" alt="Add" className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6 h-[80dvh] overflow-y-auto [&::-webkit-scrollbar]:hidden px-3">
            {loadingEducation ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="relative rounded-2xl border-gray-200 p-4 sm:p-6 md:p-8 shadow-[6px_6px_4px_0_rgba(122,90,248,0.12)] bg-white animate-pulse"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-12 sm:pr-14">
                    <div className="space-y-4">
                      <div className="h-4 w-32 rounded bg-gray-200" />
                      <div className="h-3 w-48 rounded bg-gray-200" />
                      <div className="h-4 w-24 rounded bg-gray-200" />
                      <div className="h-3 w-56 rounded bg-gray-200" />
                    </div>
                    <div className="space-y-4">
                      <div className="h-4 w-28 rounded bg-gray-200" />
                      <div className="h-3 w-44 rounded bg-gray-200" />
                      <div className="h-4 w-32 rounded bg-gray-200" />
                      <div className="h-3 w-40 rounded bg-gray-200" />
                    </div>
                  </div>
                </div>
              ))
            ) : educationList.length > 0 ? (
              educationList.map((edu, index) => (
                <div
                  key={edu.education_id || index}
                  onClick={() => handleCardClick(edu)}
                  className={`group relative rounded-2xl border-gray-200 p-4 sm:p-6 md:p-8 shadow-[6px_6px_4px_0_rgba(122,90,248,0.29)] bg-white transition ${Number(edu.education_current_studying) === 1 ? "pl-8 sm:pl-10 md:pl-8" : ""}`}
                >
                  {Number(edu.education_current_studying) === 1 && (
                    <div className="absolute h-5 w-5 sm:h-6 sm:w-6 top-2 sm:top-3 left-2 sm:left-3 flex justify-center items-center bg-[#6938EF] rounded-[8px] sm:rounded-[12px] p-1 shadow-sm">
                      <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
                        <g id="SVGRepo_iconCarrier">
                          <path d="M16.948 20.2a1 1 0 0 1-.465-.115l-4.472-2.352-4.471 2.348a1 1 0 0 1-1.451-1.054l.854-4.979-3.619-3.527a1 1 0 0 1 .554-1.706l5-.727 2.237-4.531A1 1 0 0 1 12.011 3a1 1 0 0 1 .9.558l2.236 4.53 5 .727a1 1 0 0 1 .554 1.706l-3.621 3.526.854 4.98a1 1 0 0 1-.986 1.173zM6.171 10.5l2.544 2.479a1 1 0 0 1 .285.888l-.6 3.5 3.144-1.653a.993.993 0 0 1 .931 0l3.144 1.653-.6-3.5a1 1 0 0 1 .288-.885l2.544-2.482-3.515-.511a1 1 0 0 1-.752-.547l-1.573-3.183-1.573 3.186a1 1 0 0 1-.752.547z" />
                        </g>
                      </svg>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4 pr-12 sm:pr-14">
                    <div className="space-y-2 sm:space-y-3 md:space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm md:text-lg font-semibold mb-0.5 sm:mb-1 text-black">Institute Name</label>
                        <p className="text-xs sm:text-sm text-black truncate">{edu.edu_institution_name || "—"}</p>
                      </div>
                      {formatEducationAddress(edu) !== "—" && (
                        <div>
                          <label className="block text-xs sm:text-sm md:text-lg font-semibold mb-0.5 sm:mb-1 text-black">Address</label>
                          <p className="text-xs sm:text-sm text-black ">{formatEducationAddress(edu)}</p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 sm:space-y-3 md:space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm md:text-lg font-semibold mb-0.5 sm:mb-1 text-black">Course Name</label>
                        <p className="text-xs sm:text-sm text-black truncate">{edu.course_name || "—"}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm md:text-lg font-semibold mb-0.5 sm:mb-1 text-black">Study Period</label>
                        <p className="text-xs sm:text-sm text-black">{formatStudyPeriod(edu)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, edu.education_id)}
                    className="absolute top-2 sm:top-3 right-2 sm:right-3 h-5 w-5 sm:h-6 sm:w-6 flex justify-center items-center bg-[#6B47F5] hover:bg-[#5A3EE3] rounded-[8px] sm:rounded-[12px] p-0.5 sm:p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete"
                  >
                    <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  </button>

                  {/* Colleague avatars */}
                  <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 flex items-center rounded-[8px] sm:rounded-[12px] pl-1 sm:pl-2 pr-0.5 sm:pr-1 py-0.5 sm:py-1">
                    <div className="flex -space-x-1.5 sm:-space-x-2 mr-1 sm:mr-2">
                      {edu.user_work_me?.slice(0, 3)?.map((user, idx) => (
                        <div
                          key={idx}
                          title={user.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (user.profile_image) {
                              setImagePreview({
                                url: `${process.env.REACT_APP_API_BASE_URL}${user.profile_image}`,
                                name: user.name,
                              });
                            }
                          }}
                          className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full overflow-hidden border border-sm:border-2 border-white cursor-pointer hover:scale-105 transition"
                        >
                          {user.profile_image ? (
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}${user.profile_image}`}
                              alt={user.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-[#7A5AF8] text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
                              {getInitials(user.name)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedEducation(edu); setShowViewModal(true); }}
                      className="w-6 h-6 sm:w-7 sm:h-7 md:w-7 md:h-7 bg-[#6B47F5] rounded-full flex items-center justify-center hover:bg-[#5A3EE3] transition"
                    >
                      <span className="text-white text-sm sm:text-base md:text-lg font-bold leading-none">+</span>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col h-[70dvh] justify-center items-center">
                <img src="./no-data/no-edu.svg" alt="No data" className="mx-auto mb-4 h-40 w-40" />
                <p className="font-semibold text-[28px] text-center">No education details added yet.</p>
                <p className="text-gray-500 text-center">This user hasn't added education details yet.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showViewModal && selectedEducation && (
        <ViewColleagueModal
          experience={selectedEducation}
          type="education"
          onSuccess={fetchEducationDetails}
          endpointAdd="/set_contact_add_education"
          onClose={() => setShowViewModal(false)}
        />
      )}

      {imagePreview &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4"
            onClick={() => setImagePreview(null)}
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col items-center gap-4">
                <div className="h-64 w-64 rounded-full overflow-hidden border-4 border-gray-200 flex items-center justify-center bg-gray-100">
                  <img
                    src={imagePreview.url}
                    onError={(e) => { e.target.src = "../chatbox/default.png"; }}
                    alt={imagePreview.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center">{imagePreview.name}</h3>
                <button className="w-full submit-button" onClick={() => setImagePreview(null)}>Close</button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
